/**
 * In-browser API mock for the `thresholds-carried-forward-amounts` prototype.
 *
 * Companion to src/lib/devAuth.ts. When the dev auth bypass is active (no real
 * Stytch keys / no Django backend on :8000), this patches window.fetch to serve
 * in-memory (localStorage-backed) data so the prototype is usable with no backend.
 *
 * Scope (intentionally NO revenue-statements feature — this prototype is about
 * contracts → royalty statement generation, thresholds and carried-forward amounts):
 *   - Contracts: list + create + detail (incl. read-only "scenario" fixtures that
 *     drive the create-statement panel with carried-forward + period banners).
 *   - Payees: list + detail.
 *   - Royalty statements: list + bulk-create (generate) + detail + html preview +
 *     row actions (mark sent / mark paid / delete). A few are pre-seeded as
 *     "already generated".
 *   - Label: GET/PATCH for the Settings view (default currency).
 * Any other API call returns an empty paginated response (GET) or {} (writes) so
 * the surrounding UI doesn't crash. Dev/demo convenience, NOT a backend replacement.
 *
 * Installed only from seedDevAuth() (gated on import.meta.env.DEV + placeholder
 * keys, or VITE_DEV_AUTH_BYPASS=true), so it is inert in normal production builds.
 */
import { API_BASE_URL } from "@/lib/api/core";

function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

const emptyList = { results: [], count: 0, next: null, previous: null };

/** Wrap an array as a DRF-style paginated response ({ results, count, next, previous }). */
function paginated(results: unknown[]): object {
  return { results, count: results.length, next: null, previous: null };
}

// --- Payees ----------------------------------------------------------------
const DEV_PAYEES = [
  {
    id: "payee-adele",
    name: "Adele Laurie Blue Adkins",
    legal_name: "Adele Productions LLC",
    is_active: true,
    preferred_currency: "USD",
    country: "GB",
    city: "London",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-beyonce",
    name: "Beyoncé Giselle Knowles-Carter",
    legal_name: "Parkwood Entertainment",
    is_active: true,
    preferred_currency: "USD",
    country: "US",
    city: "Houston",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-james",
    name: "James Blake Litherland",
    legal_name: null,
    is_active: true,
    preferred_currency: "USD",
    country: "GB",
    city: "London",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  // Payees referenced by the seeded royalty statements (so the payee filter lists them).
  ...["Pezet", "Sokół", "O.S.T.R.", "Taco Hemingway", "Mata"].map((name, i) => ({
    id: ["payee-pezet", "payee-sokol", "payee-ostr", "payee-taco", "payee-mata"][i],
    name,
    legal_name: null,
    is_active: true,
    preferred_currency: "USD",
    country: "PL",
    city: "Warsaw",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  })),
];

function payeeName(id: string): string {
  return DEV_PAYEES.find((p) => p.id === id)?.name ?? id;
}

// --- Scenario contracts ----------------------------------------------------
// Read-only fixtures that drive the "Create royalty statement" panel. Each
// carries carried_statements (below-threshold balances carried from prior
// periods — the carried-forward total is their sum) and has_revenue/has_expenses
// + revenue_statements_preview to drive the amber period banners.
function scenarioContract(opts: {
  id: string;
  name: string;
  has_revenue: boolean;
  has_expenses: boolean;
  revenue_statements_preview: { name: string; period: string; status: "settled" | "draft" }[];
  /**
   * Below-threshold balances carried from earlier statements. Off for the
   * contract that demos unprocessed pre-period revenue (1481): it has no prior
   * statements at all, which is exactly what makes its old revenue a
   * "never processed" balance rather than a threshold carry-forward.
   */
  carried?: boolean;
}) {
  const carried = opts.carried ?? true;
  return {
    id: opts.id,
    name: opts.name,
    status: "active",
    contract_type: "exclusive_license",
    currency: "USD",
    start_date: "2024-01-01",
    end_date: null,
    territory: "Worldwide",
    statement_period: "quarterly",
    starting_balance: null,
    // Per-contract minimum payout threshold (seeded from the label default in the
    // real flow). Drives carry-forward / below-threshold on the statements list.
    payment_threshold: "50.00",
    label: "dev-label",
    label_name: "Dev Label",
    party: "No.19 Music",
    tracks_count: 1,
    all_tracks_have_isrc: true,
    first_sound_recording_title: "Tripmastaz - While U Pretend (Original Mix)",
    can_delete: false,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    payees: [
      {
        matched: true,
        payee: { id: "payee-adele", name: "Adele Laurie Blue Adkins" },
        share_percentage: 40,
        opening_balance: null,
        // Prior below-threshold balances carried from earlier periods. Each is
        // under the 50.00 threshold and they still sum below it (34.50) — that's
        // why they're carried, not paid out yet.
        carried_statements: carried
          ? [
              { name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "18.50" },
              { name: "Apple Music Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "16.00" },
            ]
          : [],
      },
      {
        matched: true,
        payee: { id: "payee-beyonce", name: "Beyoncé Giselle Knowles-Carter" },
        share_percentage: 35,
        opening_balance: null,
        carried_statements: carried
          ? [{ name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "22.00" }]
          : [],
      },
      {
        matched: true,
        payee: { id: "payee-james", name: "James Blake Litherland" },
        share_percentage: 25,
        opening_balance: null,
        carried_statements: [],
      },
    ],
    has_revenue: opts.has_revenue,
    has_expenses: opts.has_expenses,
    revenue_statements_preview: opts.revenue_statements_preview,
    related_recordings: [],
    related_releases: [],
  };
}

const SCENARIO_PERIOD = "Oct 1, 2025 - Dec 31, 2025";

const DEV_SCENARIO_CONTRACTS = [
  scenarioContract({
    id: "roy-scn-settled",
    name: "Back in Black Agreement 1481 — Settled revenue",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
    // No prior statements → no threshold carry-forward. Its pre-period revenue
    // is genuinely unprocessed, which is the case the decision box is for.
    carried: false,
  }),
  scenarioContract({
    id: "roy-scn-draft",
    name: "Back in Black Agreement 1482 — Draft revenue",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "draft" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-no-revenue",
    name: "Back in Black Agreement 1483 — No revenue",
    has_revenue: false,
    has_expenses: true,
    revenue_statements_preview: [],
  }),
  scenarioContract({
    id: "roy-scn-no-expenses",
    name: "Back in Black Agreement 1484 — No expenses",
    has_revenue: true,
    has_expenses: false,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-none",
    name: "Back in Black Agreement 1485 — No revenue or expenses",
    has_revenue: false,
    has_expenses: false,
    revenue_statements_preview: [],
  }),
];

// --- User-created contracts (the "Add contract" flow) ----------------------
const CONTRACTS_STORE_KEY = "dev-mock-rs-contracts";

function loadContracts(): any[] {
  try {
    return JSON.parse(localStorage.getItem(CONTRACTS_STORE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveContracts(list: any[]): void {
  localStorage.setItem(CONTRACTS_STORE_KEY, JSON.stringify(list));
}

// Build a fully-shaped Contract from a POST/PUT body, merging onto an existing
// record for updates.
function buildContract(body: any, existing?: any): any {
  const now = new Date().toISOString();
  const payees = Array.isArray(body?.payees)
    ? body.payees.map((p: any) => ({
        matched: true,
        payee: { id: p.payee, name: payeeName(p.payee) },
        share_percentage: p.share_percentage ?? null,
        opening_balance: p.opening_balance ?? null,
        carried_statements: [],
      }))
    : (existing?.payees ?? []);

  return {
    id: existing?.id ?? crypto.randomUUID(),
    created_at: existing?.created_at ?? now,
    updated_at: now,
    name: body?.name ?? existing?.name ?? "Untitled contract",
    contract_type: body?.contract_type ?? existing?.contract_type ?? "exclusive_license",
    status: body?.status ?? existing?.status ?? "draft",
    start_date: body?.start_date ?? existing?.start_date ?? null,
    end_date: body?.end_date ?? existing?.end_date ?? null,
    territory: body?.territory ?? existing?.territory ?? "Worldwide",
    currency: body?.currency ?? existing?.currency ?? "USD",
    label: body?.label ?? existing?.label ?? "dev-label",
    label_name: "Dev Label",
    party: existing?.party ?? "",
    label_share_percent:
      body?.label_share_percent != null
        ? String(body.label_share_percent)
        : (existing?.label_share_percent ?? null),
    payment_threshold: body?.payment_threshold ?? existing?.payment_threshold ?? null,
    advance_amount: body?.advance_amount ?? existing?.advance_amount ?? null,
    statement_period: body?.statement_period ?? existing?.statement_period ?? "quarterly",
    starting_balance: existing?.starting_balance ?? null,
    payees,
    releases_count: existing?.releases_count ?? 0,
    tracks_count: existing?.tracks_count ?? 0,
    all_tracks_have_isrc: true,
    first_sound_recording_title: null,
    can_delete: true,
    has_revenue: false,
    has_expenses: false,
    revenue_statements_preview: [],
    related_recordings: [],
    related_releases: [],
  };
}

function findContract(id: string): any | undefined {
  return (
    DEV_SCENARIO_CONTRACTS.find((c) => c.id === id) ?? loadContracts().find((c) => c.id === id)
  );
}

// --- Contract revenue ------------------------------------------------------
// Catalogue revenue line items per contract. `statement` is the id of the
// royalty statement a line was included in, or null when it was never processed
// into any statement — that second case is what drives the "revenue from before
// the period" decision box in the create-statement flow.
//
// Only "Back in Black Agreement 1481 — Settled revenue" carries unprocessed
// pre-period revenue. Every other contract has in-period revenue only (or none),
// so the box stays specific to that demo case.
interface RevenueLine {
  id: string;
  contract: string;
  description: string;
  source: string;
  /** ISO date (yyyy-MM-dd). */
  date: string;
  amount: number;
  /** Statement id this line was billed on, or null = never processed. */
  statement: string | null;
}

const TRACK_TITLE = "Tripmastaz - While U Pretend (Original Mix)";

const DEV_REVENUE: RevenueLine[] = [
  // 1481 — in-period (Apr 1 – Jun 30, 2026): 0.62 + 0.41 + 0.23 = 1.26
  {
    id: "rev-1481-in-1",
    contract: "roy-scn-settled",
    description: TRACK_TITLE,
    source: "Spotify",
    date: "2026-04-18",
    amount: 0.62,
    statement: null,
  },
  {
    id: "rev-1481-in-2",
    contract: "roy-scn-settled",
    description: TRACK_TITLE,
    source: "Apple Music",
    date: "2026-05-09",
    amount: 0.41,
    statement: null,
  },
  {
    id: "rev-1481-in-3",
    contract: "roy-scn-settled",
    description: TRACK_TITLE,
    source: "YouTube Music",
    date: "2026-06-22",
    amount: 0.23,
    statement: null,
  },
  // 1481 — pre-period (Jan – Mar 2026): 0.07 + 0.06 + 0.05 = 0.18, never
  // included in any statement (statement: null) → triggers the decision box.
  {
    id: "rev-1481-pre-1",
    contract: "roy-scn-settled",
    description: TRACK_TITLE,
    source: "Spotify",
    date: "2026-01-22",
    amount: 0.07,
    statement: null,
  },
  {
    id: "rev-1481-pre-2",
    contract: "roy-scn-settled",
    description: TRACK_TITLE,
    source: "Apple Music",
    date: "2026-02-14",
    amount: 0.06,
    statement: null,
  },
  {
    id: "rev-1481-pre-3",
    contract: "roy-scn-settled",
    description: TRACK_TITLE,
    source: "Deezer",
    date: "2026-03-03",
    amount: 0.05,
    statement: null,
  },
  // 1482 / 1484 — in-period revenue only, so they never trigger the box. 1482
  // keeps its below-threshold carried_statements: that is the *other* path
  // (threshold carry-forward), which must stay silent.
  {
    id: "rev-1482-in-1",
    contract: "roy-scn-draft",
    description: TRACK_TITLE,
    source: "Spotify",
    date: "2026-05-11",
    amount: 24.0,
    statement: null,
  },
  {
    id: "rev-1484-in-1",
    contract: "roy-scn-no-expenses",
    description: TRACK_TITLE,
    source: "Spotify",
    date: "2026-05-11",
    amount: 18.5,
    statement: null,
  },
];

function revenueForContract(contractId: string): RevenueLine[] {
  return DEV_REVENUE.filter((r) => r.contract === contractId);
}

/**
 * Pre-period revenue the user has already resolved, per contract. `mode` mirrors
 * the decision box: "include" (default) or "exclude" (already paid elsewhere).
 */
const REVENUE_SETTLED_KEY = "dev-mock-rs-revenue-settled";

type PrePeriodMode = "include" | "exclude";

interface SettledRecord {
  mode: PrePeriodMode;
  /** Ids of the pre-period lines the decision applied to. */
  lines: string[];
}

function loadSettled(): Record<string, SettledRecord> {
  try {
    return JSON.parse(localStorage.getItem(REVENUE_SETTLED_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveSettled(map: Record<string, SettledRecord>): void {
  localStorage.setItem(REVENUE_SETTLED_KEY, JSON.stringify(map));
}

/**
 * Revenue dated before `periodStart` that was never included in a statement and
 * hasn't already been settled outside Track. Empty ⇒ no decision box.
 */
function unprocessedPrePeriodRevenue(contractId: string, periodStart: string): RevenueLine[] {
  const resolved = loadSettled()[contractId]?.lines ?? [];
  return revenueForContract(contractId).filter(
    (r) => r.date < periodStart && r.statement === null && !resolved.includes(r.id),
  );
}

function revenueInPeriod(contractId: string, start: string, end: string): RevenueLine[] {
  return revenueForContract(contractId).filter((r) => r.date >= start && r.date <= end);
}

function sumAmounts(lines: RevenueLine[]): number {
  return lines.reduce((sum, r) => sum + r.amount, 0);
}

/** Round to cents, avoiding binary-float drift on values like 0.1 + 0.2. */
function cents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Split `total` across payee shares so the rounded parts still add up to the
 * total (largest-remainder allocation) — otherwise the per-payee rows in the
 * decision box wouldn't sum to the headline figure.
 */
function splitByShares(total: number, shares: number[]): number[] {
  const totalCents = Math.round(total * 100);
  const exact = shares.map((s) => (totalCents * s) / 100);
  const floors = exact.map(Math.floor);
  let remainder = totalCents - floors.reduce((a, b) => a + b, 0);
  const order = exact
    .map((value, i) => ({ i, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floors];
  for (const { i } of order) {
    if (remainder <= 0) break;
    out[i] += 1;
    remainder -= 1;
  }
  return out.map((c) => c / 100);
}

/**
 * Everything the create-statement panel needs for one contract + period: the
 * per-payee split of in-period revenue, and of any unprocessed pre-period
 * revenue. `has_pre_period_revenue: false` ⇒ the UI shows no decision box.
 */
function prePeriodRevenueSummary(contractId: string, start: string, end: string): object {
  const contract = findContract(contractId);
  const payees = contract?.payees ?? [];
  const shares = payees.map((p: any) => p.share_percentage ?? 0);

  const periodLines = revenueInPeriod(contractId, start, end);
  const preLines = unprocessedPrePeriodRevenue(contractId, start);
  const periodTotal = cents(sumAmounts(periodLines));
  const preTotal = cents(sumAmounts(preLines));

  const periodSplit = splitByShares(periodTotal, shares);
  const preSplit = splitByShares(preTotal, shares);

  return {
    contract: contractId,
    currency: contract?.currency ?? "USD",
    payment_threshold: contract?.payment_threshold ?? null,
    period_start: start,
    period_end: end,
    period_total: periodTotal.toFixed(2),
    pre_period_total: preTotal.toFixed(2),
    has_pre_period_revenue: preLines.length > 0,
    pre_period_lines: preLines.map((r) => ({
      id: r.id,
      description: r.description,
      source: r.source,
      date: r.date,
      amount: r.amount.toFixed(2),
    })),
    payees: payees.map((p: any, i: number) => ({
      id: p.payee.id,
      name: p.payee.name,
      share_percentage: p.share_percentage,
      period_share: periodSplit[i].toFixed(2),
      pre_period_share: preSplit[i].toFixed(2),
    })),
  };
}

/**
 * The numbers behind one payee's statement for a contract + period. Shared by
 * the live preview and by the actual generate call, so the preview panel and
 * the saved statement can never disagree.
 *
 * previous_balance has two independent sources:
 *   1. carried_statements — below-threshold balances carried from prior
 *      statements. Always included, silently; unaffected by `mode`.
 *   2. unprocessed pre-period revenue — the case the decision box covers.
 *      `mode` decides how much of it lands here.
 */
function statementFigures(
  contractId: string,
  payeeId: string,
  start: string,
  end: string,
  mode: PrePeriodMode = "include",
) {
  const contract = findContract(contractId);
  const payees = contract?.payees ?? [];
  const shares = payees.map((p: any) => p.share_percentage ?? 0);
  const index = payees.findIndex((p: any) => p.payee.id === payeeId);
  const contractPayee = index === -1 ? undefined : payees[index];

  const periodLines = revenueInPeriod(contractId, start, end);
  const periodTotal = cents(sumAmounts(periodLines));
  const preLines = unprocessedPrePeriodRevenue(contractId, start);
  const preTotal = cents(sumAmounts(preLines));

  // How much of the pre-period revenue still counts as owed.
  const owedPreTotal = mode === "exclude" ? 0 : preTotal;

  const statementValue = index === -1 ? 0 : splitByShares(periodTotal, shares)[index];
  const prePeriodShare = index === -1 ? 0 : splitByShares(owedPreTotal, shares)[index];
  const carriedForward = cents(
    (contractPayee?.carried_statements ?? []).reduce(
      (sum: number, s: any) => sum + (Number.parseFloat(s.amount) || 0),
      0,
    ),
  );

  const previousBalance = cents(carriedForward + prePeriodShare);
  const balance = cents(statementValue + previousBalance);
  const threshold = Number.parseFloat(contract?.payment_threshold ?? "") || 0;

  return {
    currency: contract?.currency ?? "USD",
    contractName: contract?.name ?? "",
    payeeName: contractPayee?.payee?.name ?? "",
    sharePercentage: contractPayee?.share_percentage ?? null,
    periodStart: start,
    periodEnd: end,
    // The revenue rows behind "Total revenue", so the statement reads
    // line-by-line rather than as a lump sum.
    revenueLines: periodLines.map((r) => ({
      description: r.description,
      source: r.source,
      date: r.date,
      amount: r.amount,
    })),
    totalRevenue: periodTotal,
    statementValue,
    carriedForward,
    prePeriodShare,
    previousBalance,
    balance,
    threshold,
    belowThreshold: threshold > 0 && balance < threshold,
    preTotal,
    hasPrePeriodRevenue: preLines.length > 0,
    prePeriodLineIds: preLines.map((r) => r.id),
  };
}

type StatementFigures = ReturnType<typeof statementFigures>;

function parseMode(value: string | null): PrePeriodMode {
  return value === "exclude" ? "exclude" : "include";
}

// --- Royalty statements ----------------------------------------------------
// Bump the version suffix when the seed shape changes so the browser re-seeds
// instead of serving a stale localStorage copy.
const ROYALTY_STORE_KEY = "dev-mock-rs-royalty-v3";

const DEV_INVOICE_FILE =
  "data:text/csv;charset=utf-8,Description%2CAmount%0ANet%20royalties%20for%20period%2C4210.55";

function royaltyPayeeDetails(id: string, name: string, currency: string, country: string) {
  return {
    id,
    name,
    legal_name: `${name} LLC`,
    preferred_currency: currency,
    country,
    contact_email: `${name.split(" ")[0].toLowerCase()}@example.com`,
    is_active: true,
  };
}

function royaltyContractDetails(id: string, name: string, currency: string, threshold = "") {
  return {
    id,
    name,
    contract_type: "exclusive_license",
    status: "active",
    start_date: "2024-01-01",
    end_date: null,
    currency,
    payment_threshold: threshold,
  };
}

// "Several already generated" statements (all USD). Each (contract, payee) group
// exercises one minimum-payout-threshold state. `statement_balance` is each
// statement's OWN period amount ("Statement value"); the "Balance" column is the
// running cumulative (own + earlier unpaid carried), and the threshold is compared
// to that cumulative on the most recent unpaid statement.
//
//  1) "Test" (50.00) — Pezet: Q3 value 43.20 (carried) + Q4 value 61.40; balance grows
//     43.20 → 104.60 >= 50 → Q4 "Sent".
//  2) "Beat License" (50.00) — Sokół: Q1 value 18.00 + Q2 value 13.75; balance 18.00 →
//     31.75 < 50 → both "Carried forward", can't be marked paid.
//  3) "Sync Deal" (50.00) — O.S.T.R.: Q1 30.00 + Q2 40.00 (own periods), both Paid.
//  4) "Open Terms" (no threshold) — Taco Hemingway: 5.00, payable immediately, no chip.
//  5) "One-off" (50.00) — Mata:      62.00, payable immediately, no cascade.
const PEZET = royaltyPayeeDetails("payee-pezet", "Pezet", "USD", "PL");
const SOKOL = royaltyPayeeDetails("payee-sokol", "Sokół", "USD", "PL");
const OSTR = royaltyPayeeDetails("payee-ostr", "O.S.T.R.", "USD", "PL");
const TACO = royaltyPayeeDetails("payee-taco", "Taco Hemingway", "USD", "PL");
const MATA = royaltyPayeeDetails("payee-mata", "Mata", "USD", "PL");

const DEV_ROYALTY_STATEMENTS = [
  // 1) "Test" (50.00) — Pezet: Q4 value 61.40 + Q3 carried 43.20 → Q4 balance 104.60
  //    >= 50 → "Sent". Q3 below threshold → "Carried forward" (balance 43.20).
  {
    id: "roy-test-2",
    statement_no: "RS-2025-0102",
    payee: "payee-pezet",
    payee_details: PEZET,
    contract: "contract-test",
    contract_details: royaltyContractDetails("contract-test", "Test", "USD", "50.00"),
    period_start: "2025-10-01",
    period_end: "2025-12-31",
    statement_balance: "61.40",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-01-08T10:00:00Z",
    updated_at: "2026-01-08T10:00:00Z",
  },
  {
    id: "roy-test-1",
    statement_no: "RS-2025-0101",
    payee: "payee-pezet",
    payee_details: PEZET,
    contract: "contract-test",
    contract_details: royaltyContractDetails("contract-test", "Test", "USD", "50.00"),
    period_start: "2025-07-01",
    period_end: "2025-09-30",
    statement_balance: "43.20",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2025-10-08T10:00:00Z",
    updated_at: "2025-10-08T10:00:00Z",
  },
  // 2) "Beat License" (50.00) — Sokół: two periods both below threshold (18.00 + 13.75 =
  //    31.75 < 50) → both "Carried forward". Balance grows: 18.00 → 31.75. Statement
  //    value stays per-period (18.00 / 13.75). "Can't mark as paid" on both.
  {
    id: "roy-beat-2",
    statement_no: "RS-2026-0202",
    payee: "payee-sokol",
    payee_details: SOKOL,
    contract: "contract-beat",
    contract_details: royaltyContractDetails("contract-beat", "Beat License", "USD", "50.00"),
    period_start: "2026-04-01",
    period_end: "2026-06-30",
    statement_balance: "13.75",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-07-08T10:00:00Z",
    updated_at: "2026-07-08T10:00:00Z",
  },
  {
    id: "roy-beat-1",
    statement_no: "RS-2026-0201",
    payee: "payee-sokol",
    payee_details: SOKOL,
    contract: "contract-beat",
    contract_details: royaltyContractDetails("contract-beat", "Beat License", "USD", "50.00"),
    period_start: "2026-01-01",
    period_end: "2026-03-31",
    statement_balance: "18.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-04-08T10:00:00Z",
    updated_at: "2026-04-08T10:00:00Z",
  },
  // 3) "Sync Deal" (50.00) — O.S.T.R.: both already Paid (history).
  {
    id: "roy-sync-1",
    statement_no: "RS-2025-0301",
    payee: "payee-ostr",
    payee_details: OSTR,
    contract: "contract-sync",
    contract_details: royaltyContractDetails("contract-sync", "Sync Deal", "USD", "50.00"),
    period_start: "2025-01-01",
    period_end: "2025-12-31",
    statement_balance: "30.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2026-01-10T10:00:00Z",
    updated_at: "2026-02-01T10:00:00Z",
  },
  {
    id: "roy-sync-2",
    statement_no: "RS-2026-0302",
    payee: "payee-ostr",
    payee_details: OSTR,
    contract: "contract-sync",
    contract_details: royaltyContractDetails("contract-sync", "Sync Deal", "USD", "50.00"),
    period_start: "2026-01-01",
    period_end: "2026-12-31",
    statement_balance: "40.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2026-12-31T10:00:00Z",
    updated_at: "2027-01-15T10:00:00Z",
  },
  // 4) "Open Terms" (no threshold) — Taco Hemingway: payable immediately, no chip.
  {
    id: "roy-open-1",
    statement_no: "RS-2026-0401",
    payee: "payee-taco",
    payee_details: TACO,
    contract: "contract-open",
    contract_details: royaltyContractDetails("contract-open", "Open Terms", "USD", ""),
    period_start: "2026-03-01",
    period_end: "2026-03-31",
    statement_balance: "5.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-04-05T10:00:00Z",
    updated_at: "2026-04-05T10:00:00Z",
  },
  // 5) "One-off" (50.00) — Mata: single statement meets threshold, payable, no cascade.
  {
    id: "roy-oneoff-1",
    statement_no: "RS-2026-0501",
    payee: "payee-mata",
    payee_details: MATA,
    contract: "contract-oneoff",
    contract_details: royaltyContractDetails("contract-oneoff", "One-off", "USD", "50.00"),
    period_start: "2026-04-01",
    period_end: "2026-04-30",
    statement_balance: "62.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-05-06T10:00:00Z",
    updated_at: "2026-05-06T10:00:00Z",
  },
];

function loadRoyalty(): any[] {
  const raw = localStorage.getItem(ROYALTY_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(DEV_ROYALTY_STATEMENTS));
  return DEV_ROYALTY_STATEMENTS;
}

function saveRoyalty(list: any[]): void {
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(list));
}

let royaltySeq = 1000;
function nextStatementNo(): string {
  royaltySeq += 1;
  return `RS-2026-${String(royaltySeq).padStart(4, "0")}`;
}

// Generate one statement per payee for the selected contract + period.
function bulkCreateRoyalty(body: any): any[] {
  const contract = findContract(body?.contract);
  const period_start = body?.period_start ?? null;
  const period_end = body?.period_end ?? null;
  const payeeIds: string[] = Array.isArray(body?.payees) ? body.payees : [];
  const contractId = contract?.id ?? body?.contract ?? "";
  const mode = parseMode(body?.pre_period_mode ?? null);
  const list = loadRoyalty();
  const created = payeeIds.map((payeeId, i) => {
    // Same figures the preview showed, so the saved statement matches it.
    const figures = statementFigures(
      contractId,
      payeeId,
      period_start ?? "",
      period_end ?? "",
      mode,
    );
    const balance = figures.balance.toFixed(2);
    const name = payeeName(payeeId);
    const country = DEV_PAYEES.find((p) => p.id === payeeId)?.country ?? "US";
    return {
      id: crypto.randomUUID(),
      statement_no: nextStatementNo(),
      payee: payeeId,
      payee_details: royaltyPayeeDetails(payeeId, name, "USD", country),
      contract: contract?.id ?? body?.contract ?? null,
      contract_details: royaltyContractDetails(
        contract?.id ?? "contract",
        contract?.name ?? "Contract",
        "USD",
        contract?.payment_threshold ?? "",
      ),
      period_start,
      period_end,
      statement_balance: balance,
      currency: "USD",
      file: DEV_INVOICE_FILE,
      file_url: DEV_INVOICE_FILE,
      created_by: "dev@local.test",
      status: "draft",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // Kept so the saved statement renders the same breakdown the preview
      // showed; the pre-period revenue is resolved right after, so it can't be
      // recomputed later.
      breakdown: {
        revenueLines: figures.revenueLines,
        totalRevenue: figures.totalRevenue,
        statementValue: figures.statementValue,
        previousBalance: figures.previousBalance,
      },
      _seq: i,
    };
  });
  saveRoyalty([...created, ...list]);

  // Resolve the pre-period revenue so it can't be processed twice: whether it
  // was included in these statements or excluded as already paid, it is no
  // longer "revenue that was never processed".
  const resolvedLines = unprocessedPrePeriodRevenue(contractId, period_start ?? "").map(
    (r) => r.id,
  );
  if (resolvedLines.length > 0) {
    const settled = loadSettled();
    const previous = settled[contractId];
    settled[contractId] = {
      mode,
      lines: [...(previous?.lines ?? []), ...resolvedLines],
    };
    saveSettled(settled);
  }

  return created.map(({ _seq, ...s }) => s);
}

interface StatementTemplateData {
  title: string;
  subtitle: string;
  meta: string;
  currency: string;
  revenueLines: Array<{ description: string; source: string; date: string; amount: number }>;
  totalRevenue: number;
  statementValue: number;
  previousBalance: number;
  balance: number;
  threshold: number;
  belowThreshold: boolean;
}

/**
 * THE statement template. Used for both the saved statement (opened from the
 * royalty statements list) and the live preview in "Create new statement", so
 * the two can't drift apart. Stands in for the backend-rendered HTML the real
 * app serves from /revenue/royalty-statements/{id}/html.
 *
 * Colours are inlined because this renders inside a sandboxed iframe with no
 * access to the app's stylesheet; they mirror the --color-orange-* /
 * --color-gray-* tokens used by the in-app Alert.
 */
function statementTemplateHtml(d: StatementTemplateData): string {
  const revenueRows = d.revenueLines.length
    ? d.revenueLines
        .map(
          (line) => `<tr class="line">
            <td>${line.description}<span class="src"> · ${line.source} · ${line.date}</span></td>
            <td class="r">${money(line.amount, d.currency)}</td>
          </tr>`,
        )
        .join("")
    : `<tr class="line"><td class="src" colspan="2">No revenue in this period.</td></tr>`;

  const thresholdNote = d.belowThreshold
    ? `<p class="note">Below the ${money(d.threshold, d.currency)} minimum payout threshold —
       carried forward to next statement</p>`
    : "";

  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;color:oklch(0.21 0.006 285.885);
      padding:24px;margin:0}
    h1{font-size:16px;margin:0 0 2px;font-weight:600}
    .muted{color:oklch(0.552 0.016 285.938);font-size:12px}
    .section{margin-top:18px}
    .section-title{font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;
      color:oklch(0.552 0.016 285.938);margin:0 0 6px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    td{padding:6px 0;border-bottom:1px solid oklch(0.92 0.004 286.32)}
    td.r{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap}
    tr.line td{color:oklch(0.37 0.013 285.805)}
    .src{color:oklch(0.552 0.016 285.938);font-size:11px}
    tr.sum td{font-weight:500}
    tr.total td{font-weight:600;font-size:14px;border-bottom:none;padding-top:10px}
    .note{margin:14px 0 0;padding:8px 10px;border-radius:6px;font-size:12px;line-height:1.4;
      background:oklch(0.954 0.038 75.164);color:oklch(0.266 0.079 36.259);
      border:1px solid oklch(0.901 0.076 70.697)}
  </style></head><body>
    <h1>${d.title}</h1>
    <div class="muted">${d.subtitle}</div>
    <div class="muted">${d.meta}</div>

    <div class="section">
      <p class="section-title">Revenue</p>
      <table>
        ${revenueRows}
        <tr class="sum"><td>Total revenue</td><td class="r">${money(d.totalRevenue, d.currency)}</td></tr>
      </table>
    </div>

    <div class="section">
      <p class="section-title">Statement</p>
      <table>
        <tr><td>Statement value (payee share)</td><td class="r">${money(d.statementValue, d.currency)}</td></tr>
        <tr><td>Previous balance</td><td class="r">${money(d.previousBalance, d.currency)}</td></tr>
        <tr class="total"><td>Balance</td><td class="r">${money(d.balance, d.currency)}</td></tr>
      </table>
    </div>
    ${thresholdNote}
  </body></html>`;
}

/** Saved statement, opened from the royalty statements list. */
function royaltyStatementHtml(s: any): string {
  const currency = s.currency ?? "USD";
  const threshold = Number.parseFloat(s.contract_details?.payment_threshold ?? "") || 0;
  // Statements generated in this session carry the breakdown the preview showed.
  // The seeded fixtures predate it and only ever had a single balance, so they
  // render as that — no invented figures.
  const b = s.breakdown;
  const balance = Number.parseFloat(s.statement_balance ?? "0") || 0;

  return statementTemplateHtml({
    title: `Royalty statement ${s.statement_no ?? ""}`,
    subtitle: `${s.payee_details?.name ?? ""} · ${s.contract_details?.name ?? ""}`,
    meta: `Period: ${s.period_start ?? ""} – ${s.period_end ?? ""}`,
    currency,
    revenueLines: b?.revenueLines ?? [],
    totalRevenue: b?.totalRevenue ?? balance,
    statementValue: b?.statementValue ?? balance,
    previousBalance: b?.previousBalance ?? 0,
    balance,
    threshold,
    belowThreshold: threshold > 0 && balance < threshold,
  });
}

/** Shapes the shared template from live figures (preview + generate). */
function templateDataFromFigures(f: StatementFigures, title: string): StatementTemplateData {
  return {
    title,
    subtitle: f.contractName,
    meta:
      `Period: ${f.periodStart} – ${f.periodEnd}` +
      (f.sharePercentage != null ? ` · ${f.sharePercentage}% split` : ""),
    currency: f.currency,
    revenueLines: f.revenueLines,
    totalRevenue: f.totalRevenue,
    statementValue: f.statementValue,
    previousBalance: f.previousBalance,
    balance: f.balance,
    threshold: f.threshold,
    belowThreshold: f.belowThreshold,
  };
}

function money(value: number, currency: string): string {
  return `${currency === "USD" ? "$" : ""}${value.toFixed(2)}${currency === "USD" ? "" : ` ${currency}`}`;
}

/**
 * One preview card per payee, rendered with the same template as a saved
 * statement and from the same figures the generate call uses. Reflects
 * `pre_period_mode`, so the panel updates live as the decision changes.
 */
function previewHtml(query: URLSearchParams): string {
  const contractId = query.get("contract_id") ?? "";
  const payeeId = query.get("payee_id") ?? "";
  const start = query.get("period_start") ?? query.get("start_date") ?? "";
  const end = query.get("period_end") ?? query.get("end_date") ?? "";
  const mode = parseMode(query.get("pre_period_mode"));

  const f = statementFigures(contractId, payeeId, start, end, mode);
  return statementTemplateHtml(templateDataFromFigures(f, f.payeeName || "Payee"));
}

// --- Label (Settings view) -------------------------------------------------
const LABEL_STORE_KEY = "dev-mock-rs-label";

function loadLabel(): any {
  const raw = localStorage.getItem(LABEL_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  const label = {
    id: "dev-label",
    name: "Dev Label",
    currency: "USD",
    // Empty by default → "No minimum" (nothing blocked). Set it in Settings.
    payment_threshold: "",
    can_update_currency: true,
    country: "US",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
  };
  localStorage.setItem(LABEL_STORE_KEY, JSON.stringify(label));
  return label;
}

function saveLabel(label: any): void {
  localStorage.setItem(LABEL_STORE_KEY, JSON.stringify(label));
}

// --- Reset (prototype affordance) ----------------------------------------
// Wipes every in-memory store this prototype owns so the next load re-seeds
// from defaults: contracts, royalty statements and the label. Also clears the
// persisted org/label selection so the label currency/threshold re-initialise
// from the seed instead of a stale saved value.
export function resetPrototype(): void {
  for (const key of [
    CONTRACTS_STORE_KEY,
    ROYALTY_STORE_KEY,
    LABEL_STORE_KEY,
    REVENUE_SETTLED_KEY,
  ]) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem("organization-selection");
}

// Returns a Response for handled routes, or null to fall through to real fetch.
function route(method: string, path: string, body: any, query: URLSearchParams): Response | null {
  const trimmed = path.replace(/\/+$/, "");

  // --- Label (Settings) ----------------------------------------------------
  if (trimmed === "/accounts/labels" && method === "GET") {
    return jsonResponse(paginated([loadLabel()]));
  }
  if (trimmed.startsWith("/accounts/labels/")) {
    const id = trimmed.slice("/accounts/labels/".length);
    const label = loadLabel();
    if (id !== label.id) return jsonResponse({ detail: "Not found." }, 404);
    if (method === "GET") return jsonResponse(label);
    if (method === "PATCH" || method === "PUT") {
      const updated = { ...label, ...body, updated_at: new Date().toISOString() };
      saveLabel(updated);
      return jsonResponse(updated);
    }
  }

  // --- Payees (match BEFORE /contracts/:id) --------------------------------
  if (trimmed === "/contracts/payees" && method === "GET") {
    return jsonResponse(paginated(DEV_PAYEES));
  }
  if (trimmed.startsWith("/contracts/payees/")) {
    const id = trimmed.slice("/contracts/payees/".length);
    const found = DEV_PAYEES.find((p) => p.id === id);
    return found ? jsonResponse(found) : jsonResponse({ detail: "Not found." }, 404);
  }

  // --- Royalty statements --------------------------------------------------
  if (trimmed === "/revenue/royalty-statements/bulk-create" && method === "POST") {
    return jsonResponse(bulkCreateRoyalty(body), 201);
  }
  if (trimmed === "/revenue/royalty-statements/preview/html" && method === "GET") {
    return jsonResponse({ html: previewHtml(query) });
  }
  // Drives the "revenue from before this period" decision box.
  if (trimmed === "/revenue/royalty-statements/preview/pre-period-revenue" && method === "GET") {
    return jsonResponse(
      prePeriodRevenueSummary(
        query.get("contract_id") ?? "",
        query.get("period_start") ?? "",
        query.get("period_end") ?? "",
      ),
    );
  }
  if (trimmed === "/revenue/royalty-statements" && method === "GET") {
    let list = loadRoyalty();
    const q = (query.get("search") || query.get("q") || "").toLowerCase();
    if (q) {
      list = list.filter((s) =>
        `${s.statement_no} ${s.payee_details?.name ?? ""} ${s.contract_details?.name ?? ""}`
          .toLowerCase()
          .includes(q),
      );
    }
    const statusParam = query.get("status");
    if (statusParam) {
      const allowed = new Set(statusParam.split(","));
      list = list.filter((s) => allowed.has(s.status));
    }
    const payeesParam = query.get("payees");
    if (payeesParam) {
      const allowed = new Set(payeesParam.split(","));
      list = list.filter((s) => allowed.has(s.payee));
    }
    const after = query.get("period_start_after");
    const before = query.get("period_end_before");
    if (after) list = list.filter((s) => (s.period_start ?? "") >= after);
    if (before) list = list.filter((s) => (s.period_end ?? "") <= before);
    const ordering = query.get("ordering");
    if (ordering) {
      const desc = ordering.startsWith("-");
      const field = desc ? ordering.slice(1) : ordering;
      list = [...list].sort((a, b) => {
        const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, {
          numeric: true,
        });
        return desc ? -cmp : cmp;
      });
    }
    return jsonResponse(paginated(list));
  }
  if (trimmed.startsWith("/revenue/royalty-statements/")) {
    const rest = trimmed.slice("/revenue/royalty-statements/".length);
    const [id, sub] = rest.split("/");
    const list = loadRoyalty();
    const idx = list.findIndex((s) => s.id === id);

    if (sub === "html" && method === "GET") {
      return idx === -1
        ? jsonResponse({ detail: "Not found." }, 404)
        : jsonResponse({ html: royaltyStatementHtml(list[idx]) });
    }
    if (sub === "mark-sent" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "sent", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (sub === "mark-paid" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "paid", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (!sub) {
      if (method === "GET") {
        return idx === -1 ? jsonResponse({ detail: "Not found." }, 404) : jsonResponse(list[idx]);
      }
      if (method === "PATCH") {
        if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
        list[idx] = { ...list[idx], ...body, updated_at: new Date().toISOString() };
        saveRoyalty(list);
        return jsonResponse(list[idx]);
      }
      if (method === "DELETE") {
        if (idx !== -1) {
          list.splice(idx, 1);
          saveRoyalty(list);
        }
        return jsonResponse(null, 204);
      }
    }
  }

  // --- Contracts -----------------------------------------------------------
  if (trimmed === "/contracts") {
    if (method === "GET") {
      let list = [...DEV_SCENARIO_CONTRACTS, ...loadContracts()];
      const q = query.get("q")?.toLowerCase();
      if (q)
        list = list.filter((c) =>
          String(c.name ?? "")
            .toLowerCase()
            .includes(q),
        );
      const statusParam = query.get("status");
      if (statusParam) {
        const allowed = new Set(statusParam.split(","));
        list = list.filter((c) => allowed.has(c.status));
      }
      const ordering = query.get("ordering");
      if (ordering) {
        const desc = ordering.startsWith("-");
        const field = desc ? ordering.slice(1) : ordering;
        list = [...list].sort((a, b) => {
          const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
          return desc ? -cmp : cmp;
        });
      }
      return jsonResponse({ results: list, count: list.length, next: null, previous: null });
    }
    if (method === "POST") {
      const list = loadContracts();
      const created = buildContract(body);
      list.unshift(created);
      saveContracts(list);
      return jsonResponse(created, 201);
    }
  }
  if (trimmed.startsWith("/contracts/")) {
    const id = trimmed.slice("/contracts/".length);

    const scenario = DEV_SCENARIO_CONTRACTS.find((c) => c.id === id);
    if (scenario && method === "GET") return jsonResponse(scenario);

    const list = loadContracts();
    const idx = list.findIndex((c) => c.id === id);

    if (method === "GET") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      return jsonResponse(list[idx]);
    }
    if (method === "PUT" || method === "PATCH") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      const updated = buildContract(body, list[idx]);
      list[idx] = updated;
      saveContracts(list);
      return jsonResponse(updated);
    }
    if (method === "DELETE") {
      if (idx !== -1) {
        list.splice(idx, 1);
        saveContracts(list);
      }
      return jsonResponse(null, 204);
    }
  }

  // Unhandled API route: keep the UI alive with a benign empty payload.
  console.warn(`[rs-mock] Unhandled ${method} ${path} — returning empty response.`);
  return method === "GET" ? jsonResponse(emptyList) : jsonResponse({});
}

export function installDevApiMock(): void {
  const w = window as unknown as { __devApiMockInstalled?: boolean };
  if (w.__devApiMockInstalled) return;
  w.__devApiMockInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === "string" ? input : input instanceof Request ? input.url : String(input);

    // Only intercept calls to our API base; everything else (Vite HMR, assets) passes through.
    if (!url.startsWith(API_BASE_URL)) return originalFetch(input, init);

    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const [path, queryString = ""] = url.slice(API_BASE_URL.length).split("?");
    const query = new URLSearchParams(queryString);

    let body: any;
    const rawBody = init?.body;
    if (typeof rawBody === "string") {
      try {
        body = JSON.parse(rawBody);
      } catch {
        body = undefined;
      }
    } else if (rawBody instanceof FormData) {
      body = {};
      rawBody.forEach((value, key) => {
        body[key] = value instanceof File ? value.name : value;
      });
    }

    const handled = route(method, path, body, query);
    return handled ?? originalFetch(input, init);
  };

  console.warn("[rs-mock] In-browser contracts + royalty + settings API mock active (no backend).");
}
